/**
 * DriverPanel — the selected car at the playback cursor: identity, position,
 * gaps, laps, tyres, pit stops, penalties, telemetry, and the engine's
 * deterministic analysis for that driver when the snapshot carries it.
 * Optional comparison with a second car (shift-click in the tower).
 */
import React, { useMemo } from "react";
import { useRaceSession } from "../RaceSessionProvider";
import { formatGapRow, formatIntervalRow, formatLapTime, formatSessionClock } from "../playback";
import { TyreBadge } from "./TimingTower";

function Stat({ label, value, sub }) {
    return (
        <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-600">{label}</div>
            <div className="mt-1 text-sm text-zinc-200">{value ?? "—"}</div>
            {sub ? <div className="font-mono text-[9px] text-zinc-600">{sub}</div> : null}
        </div>
    );
}

function TelemetryBar({ label, value, max = 100, colour = "#E10600" }) {
    const numeric = Number(value);
    const percent = Number.isFinite(numeric) ? Math.max(0, Math.min(100, (numeric / max) * 100)) : 0;
    return (
        <div>
            <div className="mb-1 flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-600">
                <span>{label}</span>
                <span className="text-zinc-400">{Number.isFinite(numeric) ? Math.round(numeric) : "—"}</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/[0.06]">
                <div className="h-full rounded-full transition-all" style={{ width: `${percent}%`, background: colour }} />
            </div>
        </div>
    );
}

function latestTelemetry(rows, t) {
    if (!rows?.length) return null;
    let lo = 0;
    let hi = rows.length - 1;
    if (t < rows[0][0]) return null;
    while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (rows[mid][0] <= t) lo = mid;
        else hi = mid - 1;
    }
    const row = rows[lo];
    if (t - row[0] > 6) return null;
    return { speed: row[1], gear: row[2], throttle: row[3], brake: row[4], drs: row[5], rpm: row[6], age: t - row[0] };
}

const OUT = ["retired", "dsq", "dns"];

/** Lap cell: a car still racing is on its current lap; a finished or retired car has only completed laps. */
export function lapCell(row, raceStart) {
    const laps = row.laps_completed ?? 0;
    if (row.status === "finished") return { value: `Finished — ${laps} lap${laps === 1 ? "" : "s"}`, sub: row.finished_s != null && raceStart != null ? `took the flag at ${formatSessionClock(row.finished_s, raceStart)}` : "took the chequered flag" };
    if (OUT.includes(row.status)) return { value: `Out — ${laps} lap${laps === 1 ? "" : "s"}`, sub: row.status_note || row.status };
    return { value: row.current_lap ?? "—", sub: `${laps} completed` };
}

function DriverSummary({ row, analysis, telemetry, mode, liveCarData, raceStart = null }) {
    const colour = row.team_colour || "#E10600";
    const stint = row.stint;
    const car = mode === "live" ? liveCarData : telemetry;
    const lap = lapCell(row, raceStart);
    return (
        <div>
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                    <span className="h-6 w-1 rounded-full" style={{ background: colour }} />
                    <div>
                        <div className="font-display text-3xl leading-none tracking-tight text-white">{row.acronym}</div>
                        <div className="mt-1 text-sm text-zinc-400">{row.team_name}</div>
                        <div className="mt-0.5 text-xs text-zinc-500">
                            {row.full_name} · #{row.driver_number}
                        </div>
                    </div>
                </div>
                <div className="text-right">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-600">P{row.position ?? "—"}</div>
                    {row.grid_position ? <div className="mt-1 font-mono text-[9px] text-zinc-600">grid {row.grid_position}</div> : null}
                    <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.16em] text-zinc-500">{row.status}{row.status_note ? ` · ${row.status_note}` : ""}</div>
                    {row.lap_anomaly ? <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.16em] text-amber-200/80" title={row.lap_anomaly.note}>lap count: provider gap · {row.lap_anomaly.note}</div> : null}
                </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-x-5 gap-y-3">
                <Stat label="Gap" value={formatGapRow(row)} sub={row.gap_at ? "intervals feed" : "gap unavailable"} />
                <Stat label="Interval" value={formatIntervalRow(row)} />
                <Stat label="Lap" value={lap.value} sub={lap.sub} />
                <Stat label="Last lap" value={formatLapTime(row.last_lap?.duration_seconds)} sub={row.last_lap ? `S ${row.last_lap.sectors_seconds.map((s) => (s == null ? "—" : s.toFixed(3))).join(" · ")}` : null} />
                <Stat label="Best lap" value={formatLapTime(row.best_lap?.duration_seconds)} sub={row.best_lap ? `lap ${row.best_lap.lap_number}` : null} />
                <div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-600">Tyre</div>
                    <div className="mt-1 text-sm text-zinc-200">
                        <TyreBadge compound={stint?.compound} age={row.tyre_age_laps} />
                    </div>
                    {stint ? <div className="font-mono text-[9px] text-zinc-600">stint {stint.stint_number} · fitted age {stint.tyre_age_at_start ?? "?"}</div> : null}
                </div>
                <Stat label="Pit stops" value={row.pit_count ?? 0} sub={row.last_pit ? `last: lap ${row.last_pit.lap_number} · lane ${row.last_pit.lane_seconds ?? "?"} s · stop ${row.last_pit.stop_seconds ?? "?"} s` : null} />
                <Stat label="Penalties" value={row.penalties?.length ? row.penalties.map((p) => `${p.seconds ? `${p.seconds}s` : p.type}${p.served ? " (served)" : ""}`).join(", ") : "none"} />
            </div>
            {car ? (
                <div className="mt-4 space-y-3 border-t border-white/5 pt-4">
                    <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-600">
                        <span>Telemetry</span>
                        <span>{car.gear != null ? `gear ${car.gear}` : ""}{car.drs ? " · DRS" : ""}</span>
                    </div>
                    <TelemetryBar label="Speed km/h" value={car.speed} max={360} colour={colour} />
                    <TelemetryBar label="Throttle" value={car.throttle} max={100} colour="#22c55e" />
                    <TelemetryBar label="Brake" value={car.brake ? 100 : 0} max={100} colour="#f97316" />
                </div>
            ) : (
                <div className="mt-4 border-t border-white/5 pt-4 text-xs text-zinc-500">Telemetry not available at this moment.</div>
            )}
            {analysis ? <AnalysisBlock analysis={analysis} /> : null}
        </div>
    );
}

function AnalysisBlock({ analysis }) {
    const pace = analysis.pace || {};
    const gap = analysis.gap_trend || {};
    const pit = analysis.pit || {};
    return (
        <div className="mt-4 space-y-3 border-t border-white/5 pt-4">
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-600">Analysis</div>
            <div className="grid grid-cols-2 gap-x-5 gap-y-3">
                <Stat label="Clean-lap pace" value={pace.median_seconds != null ? formatLapTime(pace.median_seconds) : "—"} sub={pace.sample_size != null ? `${pace.sample_size} laps · ±${pace.spread_seconds ?? "?"} s` : pace.reason} />
                <Stat label="vs leader pace" value={pace.delta_to_leader_seconds != null ? `${pace.delta_to_leader_seconds > 0 ? "+" : ""}${pace.delta_to_leader_seconds.toFixed(3)} s` : "—"} />
                <Stat label="Gap trend" value={gap.per_lap_seconds != null ? `${gap.per_lap_seconds > 0 ? "+" : ""}${gap.per_lap_seconds.toFixed(2)} s/lap` : "—"} sub={gap.window_laps ? `last ${gap.window_laps} laps vs ${gap.reference}` : gap.reason} />
                <Stat label="Pit loss est." value={pit.estimated_loss_seconds != null ? `${pit.estimated_loss_seconds.toFixed(1)} s` : "—"} sub={pit.basis} />
                <Stat label="Positions by cause" value={analysis.position_changes ? Object.entries(analysis.position_changes).map(([k, v]) => `${k.replace(/_/g, " ")} ${v > 0 ? "+" : ""}${v}`).join(", ") || "none" : "—"} />
                <Stat label="Degradation" value={analysis.degradation?.seconds_per_lap != null ? `${analysis.degradation.seconds_per_lap.toFixed(3)} s/lap` : "—"} sub={analysis.degradation?.reason || (analysis.degradation ? `${analysis.degradation.sample_size} laps` : null)} />
            </div>
            {analysis.notes?.length ? <ul className="list-disc space-y-1 pl-4 text-xs leading-relaxed text-zinc-500">{analysis.notes.map((note) => <li key={note}>{note}</li>)}</ul> : null}
        </div>
    );
}

export default function DriverPanel({ className = "" }) {
    const { snapshot, selectedEntryId, compareEntryId, telemetryFor, cursorRef, mode, setCompareEntry, manifest } = useRaceSession();
    const raceStart = manifest?.time?.race_start_seconds ?? null;
    const entryList = snapshot?.entries;
    const rows = useMemo(() => entryList || [], [entryList]);
    const row = useMemo(() => rows.find((item) => item.entry_id === selectedEntryId) || null, [rows, selectedEntryId]);
    const compareRow = useMemo(() => rows.find((item) => item.entry_id === compareEntryId) || null, [rows, compareEntryId]);
    const t = cursorRef.current.cursor;
    const analysisFor = (entryId) => snapshot?.analysis?.drivers?.[entryId] || null;
    if (!row) {
        return (
            <div className={`rounded-[28px] border border-white/8 bg-white/[0.02] px-4 py-5 ${className}`} data-testid="driver-panel">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-600">Selected driver</div>
                <p className="mt-3 text-sm leading-relaxed text-zinc-500">Select a car on the map or in the timing tower. Shift-click a second car to compare.</p>
            </div>
        );
    }
    return (
        <div className={`rounded-[28px] border border-white/8 bg-white/[0.02] px-4 py-4 ${className}`} data-testid="driver-panel" onClick={(event) => event.stopPropagation()}>
            <DriverSummary row={row} analysis={analysisFor(row.entry_id)} telemetry={latestTelemetry(telemetryFor(row.entry_id), t)} liveCarData={row.car_data} mode={mode} raceStart={raceStart} />
            {compareRow ? (
                <div className="mt-5 border-t border-white/10 pt-4">
                    <div className="mb-3 flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.22em] text-sky-200/80">
                        <span>Compare</span>
                        <button type="button" className="text-zinc-500 hover:text-white" onClick={() => setCompareEntry(null)}>
                            clear
                        </button>
                    </div>
                    <DriverSummary row={compareRow} analysis={analysisFor(compareRow.entry_id)} telemetry={latestTelemetry(telemetryFor(compareRow.entry_id), t)} liveCarData={compareRow.car_data} mode={mode} raceStart={raceStart} />
                </div>
            ) : null}
        </div>
    );
}
